import { createContext, useContext, useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useAuth } from './AuthContext';

import { API } from '@/lib/apiBase';

const CartContext = createContext(null);

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
};

const getBundleKey = (bundle) => (
  bundle.student_id ? `${bundle.bundle_id}_${bundle.student_id}` : bundle.bundle_id
);

export const CartProvider = ({ children }) => {
  const { user, token } = useAuth();
  const syncTimerRef = useRef(null);

  const [items, setItems] = useState(() => {
    const saved = localStorage.getItem('cart');
    return saved ? JSON.parse(saved) : [];
  });

  const [printItems, setPrintItems] = useState(() => {
    const saved = localStorage.getItem('cart_prints');
    return saved ? JSON.parse(saved) : [];
  });

  const [shippingAddress, setShippingAddress] = useState(() => {
    const saved = localStorage.getItem('cart_shipping');
    return saved ? JSON.parse(saved) : { name: '', street: '', city: '', postal_code: '', country: 'Schweiz' };
  });

  const [bundleItems, setBundleItems] = useState(() => {
    const saved = localStorage.getItem('cart_bundles');
    if (saved) return JSON.parse(saved);
    // Migrate from old single bundleItem
    const oldBundle = localStorage.getItem('cart_bundle');
    if (oldBundle) {
      const parsed = JSON.parse(oldBundle);
      if (parsed) return [parsed];
    }
    return [];
  });

  useEffect(() => { localStorage.setItem('cart', JSON.stringify(items)); }, [items]);
  useEffect(() => { localStorage.setItem('cart_prints', JSON.stringify(printItems)); }, [printItems]);
  useEffect(() => { localStorage.setItem('cart_shipping', JSON.stringify(shippingAddress)); }, [shippingAddress]);
  useEffect(() => {
    localStorage.setItem('cart_bundles', JSON.stringify(bundleItems));
    localStorage.removeItem('cart_bundle');
  }, [bundleItems]);

  // Debounced server-side cart sync (for abandoned-cart tracking)
  useEffect(() => {
    if (!user || !token) return;

    if (syncTimerRef.current) clearTimeout(syncTimerRef.current);
    syncTimerRef.current = setTimeout(() => {
      const total =
        items.reduce((s, i) => s + (i.price || 0), 0) +
        printItems.reduce((s, i) => s + (i.format_price || 0) * (i.quantity || 1), 0) +
        bundleItems.reduce((s, b) => s + (b.discounted_price || 0), 0);

      fetch(`${API}/cart/sync`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        credentials: 'include',
        body: JSON.stringify({
          items,
          print_items: printItems,
          bundle_items: bundleItems,
          total,
        }),
      }).catch(() => { /* non-blocking */ });
    }, 1500);

    return () => {
      if (syncTimerRef.current) clearTimeout(syncTimerRef.current);
    };
  }, [items, printItems, bundleItems, user, token]);

  // Digital photo cart
  const addToCart = useCallback((photo) => {
    setItems(prev => {
      if (prev.find(item => item.photo_id === photo.photo_id)) return prev;
      return [...prev, photo];
    });
  }, []);

  const removeFromCart = useCallback((photoId) => {
    setItems(prev => prev.filter(item => item.photo_id !== photoId));
  }, []);

  const isInCart = useCallback((photoId) => (
    items.some(item => item.photo_id === photoId)
  ), [items]);

  // Print cart
  const addPrintItem = useCallback((photo, format) => {
    setPrintItems(prev => {
      const key = `${photo.photo_id}_${format.format_id}`;
      const existing = prev.find(item => item.key === key);
      if (existing) {
        return prev.map(item =>
          item.key === key ? { ...item, quantity: item.quantity + 1 } : item
        );
      }
      return [...prev, {
        key,
        photo_id: photo.photo_id,
        format_id: format.format_id,
        format_name: format.name,
        format_price: format.price,
        quantity: 1,
        watermarked_url: photo.watermarked_url,
      }];
    });
  }, []);

  const removePrintItem = useCallback((key) => {
    setPrintItems(prev => prev.filter(item => item.key !== key));
  }, []);

  const updatePrintQuantity = useCallback((key, quantity) => {
    if (quantity < 1) {
      setPrintItems(prev => prev.filter(item => item.key !== key));
      return;
    }
    setPrintItems(prev =>
      prev.map(item => item.key === key ? { ...item, quantity } : item)
    );
  }, []);

  const clearCart = useCallback(() => {
    setItems([]);
    setPrintItems([]);
    setBundleItems([]);
    // Also clear server-side cart so it doesn't show up in abandoned-carts admin view
    if (user && token) {
      fetch(`${API}/cart`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
        credentials: 'include',
      }).catch(() => {});
    }
  }, [user, token]);

  // Bundle cart — per-child bundles
  const addBundleToCart = useCallback((bundle) => {
    const key = getBundleKey(bundle);
    setBundleItems(prev => {
      if (prev.find(b => getBundleKey(b) === key)) return prev;
      return [...prev, {
        bundle_id: bundle.bundle_id,
        name: bundle.name,
        class_name: bundle.class_name,
        student_id: bundle.student_id || null,
        student_name: bundle.student_name || null,
        photo_count: bundle.photo_count,
        photo_ids: bundle.photo_ids,
        original_price: bundle.original_price,
        discounted_price: bundle.discounted_price,
        discount_percent: bundle.discount_percent,
        fixed_price: bundle.fixed_price || null,
        savings: bundle.savings,
      }];
    });
    // Remove individual photos that are in this bundle from cart
    const bundlePhotoIds = bundle.photo_ids || [];
    setItems(prev => prev.filter(item => !bundlePhotoIds.includes(item.photo_id)));
  }, []);

  const removeBundleFromCart = useCallback((key) => {
    setBundleItems(prev => prev.filter(b => getBundleKey(b) !== key));
  }, []);

  const isBundleInCart = useCallback((bundle) => {
    const key = getBundleKey(bundle);
    return bundleItems.some(b => getBundleKey(b) === key);
  }, [bundleItems]);

  const getDigitalTotal = useCallback(() => (
    items.reduce((sum, item) => sum + (item.price || 0), 0)
  ), [items]);

  const getPrintTotal = useCallback(() => (
    printItems.reduce((sum, item) => sum + (item.format_price * item.quantity), 0)
  ), [printItems]);

  const getBundleTotal = useCallback(() => (
    bundleItems.reduce((sum, b) => sum + b.discounted_price, 0)
  ), [bundleItems]);

  const getTotal = useCallback(() => (
    getDigitalTotal() + getPrintTotal() + getBundleTotal()
  ), [getDigitalTotal, getPrintTotal, getBundleTotal]);

  const getItemCount = useCallback(() => (
    items.length + printItems.length + bundleItems.length
  ), [items, printItems, bundleItems]);

  const hasPrints = useCallback(() => (
    printItems.length > 0
  ), [printItems]);

  const value = useMemo(() => ({
    items,
    printItems,
    bundleItems,
    shippingAddress,
    setShippingAddress,
    addToCart,
    removeFromCart,
    isInCart,
    addPrintItem,
    removePrintItem,
    updatePrintQuantity,
    addBundleToCart,
    removeBundleFromCart,
    isBundleInCart,
    getBundleKey,
    clearCart,
    getTotal,
    getDigitalTotal,
    getPrintTotal,
    getBundleTotal,
    getItemCount,
    hasPrints,
  }), [
    items, printItems, bundleItems, shippingAddress,
    addToCart, removeFromCart, isInCart,
    addPrintItem, removePrintItem, updatePrintQuantity,
    addBundleToCart, removeBundleFromCart, isBundleInCart,
    clearCart, getTotal, getDigitalTotal, getPrintTotal, getBundleTotal,
    getItemCount, hasPrints,
  ]);

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};
